import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Chat from './Chat/ChatContainer';
import Notes from './Notes/NotesContainer';
import * as actions from '../../store/actions';
import * as streamActions from '../../store/actions/InterviewRecordingActions';
import NTInterviewVideoContainer from '../../components/Interview/NTInterviewVideoContainer.js';
import MeetingControlBar from '../../components/Meeting/MeetingControlBar.js';
import ScreenShareView from '../Interview/Meeting/ScreenShareView.js';
import { isInterviewDeveloper } from '../../helperFunctions';
import { useInterviewCommonLogic } from '../../hooks/useInterviewCommonLogic';

// Random questions database - customize these for your interviewer
const interviewQuestions = [
  'Tell me about yourself.',
  'What are your strengths?',
  'What is your greatest weakness?',
  'Why do you want this job?',
  'Where do you see yourself in 5 years?',
  'Can you describe a challenging situation at work and how you handled it?',
  'How do you prioritize tasks?',
  'What motivates you?',
  'Can you explain a technical concept to a non-technical person?',
  'How do you handle feedback?',
  'What interests you about this company?',
  'Describe a time you failed and what you learned.',
  'How do you work in a team?',
  'What are your career goals?',
  'Why should we hire you?',
];

// Function to get random question
function getRandomQuestion() {
  const randomIndex = Math.floor(Math.random() * interviewQuestions.length);
  return interviewQuestions[randomIndex];
}

// Function to send question to LiveTalking
async function askRandomQuestion(sessionId) {
  if (!sessionId) {
    console.error('Session ID is required');
    return false;
  }

  try {
    const question = getRandomQuestion();
    console.log('Asking random question:', question);

    const response = await fetch(`https://livetalking-api-qa.codehall.in/human`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        text: question,
        type: 'echo', // Use 'echo' to directly speak the question
        interrupt: true, // Interrupt if already speaking
        sessionid: sessionId,
        tts: {
          ref_file: 'zh-CN-YunxiaNeural', // Adjust voice as needed
          emotion: 'neutral', // Can be 'happy', 'sad', 'angry' (if supported)
        },
      }),
    });

    if (!response.ok) {
      console.error('Error asking question:', response.status, response.statusText);
      return false;
    }

    console.log('Question sent successfully');
    return true;
  } catch (error) {
    console.error('Failed to ask question:', error);
    return false;
  }
}

// Function to ask random question with delay
async function askRandomQuestionWithDelay(sessionId, delay = 2000) {
  await new Promise(resolve => setTimeout(resolve, delay));
  return await askRandomQuestion(sessionId);
}

// Auto-ask questions sequence
async function startQuestionSequence(sessionId, interval = 15000) {
  let isActive = true;
  let questionCount = 0;
  const maxQuestions = 5; // Limit to 5 questions

  while (isActive && questionCount < maxQuestions) {
    const success = await askRandomQuestion(sessionId);

    if (success) {
      questionCount++;
      console.log(`Question ${questionCount} of ${maxQuestions} asked`);

      // Wait for user response time + buffer
      await new Promise(resolve => setTimeout(resolve, interval));
    } else {
      console.error('Question failed, stopping sequence');
      isActive = false;
    }
  }

  if (questionCount >= maxQuestions) {
    console.log('Question sequence completed');
  }

  return questionCount;
}

// Stop question sequence (optional)
// eslint-disable-next-line no-unused-vars
function stopQuestionSequence() {
  // eslint-disable-next-line no-undef
  isActive = false;
}

const AIInterviewContainer = props => {
  const { t } = useTranslation('interview_container');
  const isDeveloper = isInterviewDeveloper(props.participants);
  const [sessionId, setSessionId] = useState(null);

  const {
    chat,
    currOption,
    setCurrOption,
    isWSConnected,
    isScreenShareView,
    curr_note,
    setCurrNoteState,
    inactivityModalVisible,
    countdown,
    contextHolder,
    sendMessage,
    toggleView,
    handleRecoveryModalOk,
    handleContinue,
    handleDisconnect,
    handleEndInterview,
  } = useInterviewCommonLogic(props);

  // Effect to start question sequence if user is a developer and session ID is available
  useEffect(() => {
    if (isDeveloper && props.webrtcSessionId) {
      console.log('WebRTC session ID received:', props.webrtcSessionId);
      setSessionId(props.webrtcSessionId);
      startQuestionSequence(props.webrtcSessionId);
    }
  }, [isDeveloper, props.webrtcSessionId]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 85px)',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isScreenShareView ? (
          <ScreenShareView />
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              boxSizing: 'border-box',
              overflow: 'hidden',
              gap: '16px',
            }}
          >
            <div
              style={{
                flex: 1,
                background: '#202124',
                overflow: 'hidden',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Display Chime video tiles which will include the WebRTC stream */}
              <NTInterviewVideoContainer />
            </div>

            {(currOption === 'chat' || currOption === 'notes') && (
              <div
                style={{
                  width: '400px',
                  height: '100%',
                  background: 'transparent',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ padding: '16px', height: '100%', overflow: 'hidden' }}>
                  {currOption === 'chat' && (
                    <Chat
                      sendMessage={sendMessage}
                      chat={chat}
                      connected={isWSConnected}
                      chat_data={props.chat_data}
                      unreadCount={props.unreadCount}
                    />
                  )}
                  {currOption === 'notes' && (
                    <Notes
                      curr_note={curr_note}
                      setCurrNoteState={setCurrNoteState}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <MeetingControlBar
        setCurrOption={setCurrOption}
        isDeveloper={isInterviewDeveloper(props.participants)}
        onScreenShareClick={() => toggleView()}
        onEndMeeting={handleEndInterview}
        isScreenShareActive={isScreenShareView}
        currOption={currOption}
        interviewType={props.interview_type}
      />
      {contextHolder}
      {props.recoveryModalOpen && (
        <Modal
          title={t('nt_interview_container.screen_share_stopped_title')}
          open={props.recoveryModalOpen}
          onOk={handleRecoveryModalOk}
          closable={false}
          okText={t('nt_interview_container.share_screen_again')}
        >
          <p>{t('nt_interview_container.screen_share_stopped_message')}</p>
        </Modal>
      )}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            {t('nt_interview_container.interviewer_left_meeting_title')}
          </div>
        }
        open={inactivityModalVisible}
        onOk={handleContinue}
        onCancel={handleDisconnect}
        okText={t('nt_interview_container.continue_interview')}
        cancelText={t('nt_interview_container.end_interview')}
        closable={false}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <p>{t('nt_interview_container.interviewer_left_meeting_message')}</p>
          <p>
            {t('nt_interview_container.time_remaining')}: <strong>{countdown}</strong>{' '}
            {t('nt_interview_container.seconds')}
          </p>
        </div>
      </Modal>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    fetchInterviewInvite: (
      interview_id,
      hash_value,
      role,
      company_id,
      fetch_interview_data,
      callback,
    ) =>
      dispatch(
        actions.fetchInterviewInvite(
          interview_id,
          hash_value,
          role,
          company_id,
          fetch_interview_data,
          callback,
        ),
      ),
    fetchData: interview_id => dispatch(actions.fetchData(interview_id)),
    setEndInterviewUserDetail: user => dispatch(actions.setEndInterviewUserDetail(user)),
    setParticipants: participants => dispatch(actions.setParticipants(participants)),
    postNote: (data, callback) => dispatch(actions.postNote(data, callback)),
    checkNetworkConnection: () => dispatch(actions.checkNetworkConnection()),
    setHeartbeat: fire_time => dispatch(actions.setHeartbeat(fire_time)),
    setUnreadCount: count => dispatch(actions.setUnreadCount(count)),
    closeMediaRecoveryModal: () => dispatch(streamActions.closeMediaRecoveryModal()),
    initScreenStream: () => dispatch(streamActions.initScreenStream()),
    expireMeeting: (meetingId, isDeveloper) => {
      dispatch(actions.expireMeeting(meetingId, isDeveloper));
    },
  };
};

const mapStateToProps = state => {
  return {
    interview_id: state.interview.interview_id,
    interview_status: state.interview.status,
    interview_title: state.interview.title,
    interview_role: state.interview.role,
    interview_type: state.interview.interview_type,
    participants: state.interview.participants,
    start_time: state.interview.start_time,
    role: state.interview.role,
    user_id: state.auth.user_id,
    chat_data: state.events.chat_data,
    heartbeat: state.events.heartbeat,
    meetingSession: state.interviewRecording.meetingSession,
    unreadCount: state.events.unreadCount,
    recoveryModalOpen: state.interviewRecording.recoveryModalOpen,
    recoveryReason: state.interviewRecording.recoveryReason,
    meeting: state.meeting,
    webrtcSessionId: state.interviewRecording.webrtcSessionId,
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AIInterviewContainer));
